const DATABASE_NAME = 'vgen-local-video-library';
const STORE_NAME = 'media';
const INDEX_KEY = 'vgen.local-video-index.v1';

export type LocalVideoJob = {
  id: string;
  user_id: string;
  status: string;
  model_id: string;
  prompt: string;
  aspect_ratio: string;
  duration_seconds: number;
  created_at: string;
  [key: string]: unknown;
};

type StoredMedia = { id: string; video: Blob; frame?: Blob };
type IndexEntry = { job: LocalVideoJob; serverDeletionPending: boolean };

function readIndex(): IndexEntry[] {
  try {
    const value = localStorage.getItem(INDEX_KEY);
    return value ? JSON.parse(value) as IndexEntry[] : [];
  } catch {
    return [];
  }
}

function writeIndex(entries: IndexEntry[]): void {
  localStorage.setItem(INDEX_KEY, JSON.stringify(entries));
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Не удалось открыть локальную библиотеку'));
  });
}

async function transact<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const request = action(transaction.objectStore(STORE_NAME));
      let result:T;
      request.onsuccess = () => {result=request.result};
      transaction.oncomplete = () => resolve(result);
      request.onerror = () => reject(request.error ?? new Error('Ошибка локального хранилища'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Ошибка локального хранилища'));
    });
  } finally {
    database.close();
  }
}

export async function saveLocalVideo(job: LocalVideoJob, video: Blob, frame?: Blob): Promise<void> {
  await transact('readwrite', (store) => store.put({ id: job.id, video, ...(frame ? { frame } : {}) } satisfies StoredMedia));
  const readback=await getLocalVideo(job.id);
  if(!readback||readback.video.size!==video.size)throw new Error('Не удалось проверить локальную копию. Серверная копия не удалена.');
  const index = readIndex().filter((entry) => entry.job.id !== job.id);
  writeIndex([{ job, serverDeletionPending: true }, ...index]);
}

export async function getLocalVideo(jobId: string): Promise<{ video: Blob; frame?: Blob } | null> {
    const stored = await transact<StoredMedia | undefined>('readonly', (store) => store.get(jobId));
    return stored ? { video: stored.video, ...(stored.frame ? { frame: stored.frame } : {}) } : null;
}

export async function listLocalVideos(userId?: string): Promise<Array<IndexEntry & { available: boolean|null }>> {
  const entries = readIndex().filter((entry) => !userId || entry.job.user_id === userId);
  return Promise.all(entries.map(async(entry)=>{try{return {...entry,available:(await transact('readonly',store=>store.count(entry.job.id)))>0}}catch{return {...entry,available:null}}}));
}

export function markServerMediaDeleted(jobId: string): void {
  writeIndex(readIndex().map((entry) => entry.job.id === jobId ? { ...entry, serverDeletionPending: false } : entry));
}

export async function deleteLocalVideo(jobId: string): Promise<void> {
  await transact('readwrite', (store) => store.delete(jobId));
  writeIndex(readIndex().filter((entry) => entry.job.id !== jobId));
}
